import { CLIENT_ID_HEADER, CSRF_HEADER, type ApiErrorBody } from "@piano/shared";

/** Fehler einer API-Anfrage mit stabilem Fehlercode vom Server. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

let csrfToken: string | null = null;
let onUnauthorized: (() => void) | null = null;
let refreshCsrf: (() => Promise<boolean>) | null = null;

/** Wird vom Session-Store gesetzt. */
export function configureApi(options: {
  csrfToken: string | null;
  onUnauthorized?: () => void;
  refreshCsrf?: () => Promise<boolean>;
}): void {
  csrfToken = options.csrfToken;
  if (options.onUnauthorized) onUnauthorized = options.onUnauthorized;
  if (options.refreshCsrf) refreshCsrf = options.refreshCsrf;
}

/** Kennung dieses Tabs (für Live-Events und die Live-Session). */
export const clientId: string = (() => {
  const key = "piano:client-id";
  try {
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const created = crypto.randomUUID();
    sessionStorage.setItem(key, created);
    return created;
  } catch {
    return crypto.randomUUID();
  }
})();

/** Absolute URL zu einem API-Pfad – relativ zum <base href> (BASE_PATH). */
export function apiUrl(path: string): string {
  return new URL(path.replace(/^\//, ""), document.baseURI).toString();
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface RequestOptions {
  headers?: Record<string, string>;
  keepalive?: boolean;
  signal?: AbortSignal;
  /** Rohdaten statt JSON (z. B. PDF-Upload) */
  raw?: BodyInit;
}

async function parseError(response: Response): Promise<ApiError> {
  let body: Partial<ApiErrorBody> = {};
  try {
    body = (await response.json()) as ApiErrorBody;
  } catch {
    // keine JSON-Antwort (z. B. Proxy-Fehler)
  }
  return new ApiError(
    response.status,
    body.error ?? `HTTP_${response.status}`,
    body.message ?? "Die Anfrage ist fehlgeschlagen",
    body.details,
  );
}

export async function request<T>(
  method: Method,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
  retried = false,
): Promise<T> {
  const headers: Record<string, string> = { [CLIENT_ID_HEADER]: clientId, ...options.headers };
  if (method !== "GET" && csrfToken) headers[CSRF_HEADER] = csrfToken;
  let payload: BodyInit | undefined = options.raw;
  if (body !== undefined && payload === undefined) {
    headers["content-type"] = "application/json";
    payload = JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      method,
      headers,
      body: payload,
      credentials: "same-origin",
      cache: "no-store",
      ...(options.keepalive ? { keepalive: true } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if ((error as Error).name === "AbortError") throw error;
    throw new ApiError(0, "NETWORK", "Keine Verbindung zum Server");
  }

  if (response.ok) {
    if (response.status === 204) return undefined as T;
    const type = response.headers.get("content-type") ?? "";
    return (type.includes("application/json") ? await response.json() : undefined) as T;
  }

  const error = await parseError(response);
  if (error.status === 401 && !path.startsWith("api/auth/login")) onUnauthorized?.();
  if (error.status === 403 && error.code === "CSRF" && !retried && refreshCsrf && (await refreshCsrf())) {
    return request<T>(method, path, body, options, true);
  }
  throw error;
}

/** Upload mit Fortschritt (XHR, da fetch keinen Upload-Fortschritt kennt). */
export function uploadWithProgress<T>(
  path: string,
  file: Blob,
  headers: Record<string, string>,
  onProgress: (fraction: number) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", apiUrl(path));
    xhr.withCredentials = true;
    xhr.setRequestHeader("content-type", "application/pdf");
    xhr.setRequestHeader(CLIENT_ID_HEADER, clientId);
    if (csrfToken) xhr.setRequestHeader(CSRF_HEADER, csrfToken);
    for (const [key, value] of Object.entries(headers)) xhr.setRequestHeader(key, value);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    };
    xhr.onerror = () => reject(new ApiError(0, "NETWORK", "Keine Verbindung zum Server"));
    xhr.onload = () => {
      let body: unknown;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        body = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) return resolve(body as T);
      const err = (body ?? {}) as Partial<ApiErrorBody>;
      reject(
        new ApiError(xhr.status, err.error ?? `HTTP_${xhr.status}`, err.message ?? "Upload fehlgeschlagen"),
      );
    };
    xhr.send(file);
  });
}
